import { makeStyles } from '@material-ui/core';
import arrayMutators from 'final-form-arrays';
import { useSnackbar } from 'notistack';
import { pipe } from 'ramda';
import React, { useEffect, useState, useMemo } from 'react';
import { Form } from 'react-final-form';
import { Button, FormButtons, PageLoader } from '.';
import { CustomQuestion } from '../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../api/CustomQuestion/types/Type';
import authAPI, { ConsentType, ICheckVentureValidate } from '../../api/auth';
import { TenantId } from '../../api/tenants/Tenant';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import { Address, City, Country, Name, State, URL } from '../../types';
import { apply, isLeft, left, parseObject, right } from '../../utils/Either';
import * as Email from '../../utils/String/Email';
import { stringToEitherEmail } from '../../utils/String/Email';
import { Phone, stringToEitherPhone } from '../../utils/String/Phone';
import * as Sentance from '../../utils/String/Sentence';
import { fromString, Sentence } from '../../utils/String/Sentence';
import { isUrl } from '../../utils/String/Url';
import { stringToZip, Zip } from '../../utils/String/Zip';
import {
  getCustomQuestionsValidation,
  getCustomQuestionsValidationSchema,
} from '../../utils/custom-questions';
import { uuidv4 } from '../../utils/functions';
import { lengthField } from '../../utils/form';
import { length } from '../../utils/wysiwyg';
import { yupValidate } from '../../utils/yup';

interface Props {
  onSubmit: (values: FormValues) => void;
  onDone: () => Promise<void>;
  tenantId: TenantId;
  initialValues?: any;
  children: React.ReactNode[];
  isLoading: boolean;
  isUploading: boolean;
  consents: ConsentType[];
  showCustomAdditionalInfo?: boolean;
  additionalInfoCustomQuestions?: CustomQuestion[] | null;
  showSuccess: () => void;
  founderCommitmentEnabled?: boolean;
  goalIntegrationEnabled?: boolean;
  showFounderCustomQuestions?: boolean;
  founderCustomQuestions?: CustomQuestion[] | null;
}

interface PropsPage {
  children: React.ReactNode;
}

interface FounderForm {
  firstName: string | Name;
  lastName: string | Name;
  tempId?: string;
  email: string | Email.Email;
  emailConfirm: string | Email.Email;
  phone: string | null | Phone;
  linkedInProfile: string | null | Sentence<250>;
  gender: string | Sentence<20> | null;
  race: string | Sentence<50> | null;
  veteran: string;
  about: string;
  currentRole: string;
  education: string;
  isSerialFounder: string;
}

interface ValidFounderForm extends FounderForm {
  firstName: Name;
  lastName: Name;
  email: Email.Email;
  emailConfirm: Email.Email;
  phone: Phone | null;
  linkedInProfile: null | Sentence<250>;
  gender: Sentence<20> | null;
  race: Sentence<50> | null;
  veteran: string;
}

export interface FormValues {
  // step 1
  ventureName: string;
  url: string | URL;
  address: string | Address;
  city: string | City;
  state: string | State;
  zip: string | Zip;
  country: string | Country;
  specializations: string;
  ventureStage: string;
  businessModel: string;
  description: string;
  mentorshipNeeds: string;
  matchingNotes: string;

  // step 2
  businessDescription: string;
  numOfEmployees: string;
  isTech: string;
  currentProgress: string;
  requiredSupport: string;

  // step 3
  founders: Array<FounderForm>;

  //step4
  howDidYouFind: string;
  legally: string;
  marketSize: string;
  reasonToStartNow: string;
  plans: string;
  growthRatio: string;
  dedication: string;
  yearPlans: string;
  obstacles: string;
  mentorshipPlans: string;
  comments: string;
}

type Errors = {
  [x: string]: string | Array<FounderErrors | null>;
};

type FounderErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  emailConfirm?: string;
  race?: string;
  gender?: string;
  veteran?: string;
  currentRole?: string;
  education?: string;
  isSerialFounder?: string;
};

const validateFounder = (founder: FounderForm) => {
  const r = parseObject<
    FounderForm,
    ValidFounderForm,
    Partial<Record<keyof FounderForm, string>>
  >(
    {
      email: (v) => stringToEitherEmail(v || ''),
      emailConfirm: pipe(
        (v) => v || '',
        stringToEitherEmail,
        apply(left, (email) =>
          email === founder.email ? right(email) : left('Emails do not match'),
        ),
      ),
      firstName: (v) => fromString(256, v || ''),
      lastName: (v) => fromString(256, v || ''),
      phone: (v) => (v ? stringToEitherPhone(v) : right(null)),
      linkedInProfile: (v) =>
        v ? (isUrl(v) ? fromString(250, v) : left('Invalid URL')) : right(null),
      gender: (v) => (v ? fromString(20, v) : right(null)),
      race: (v) => (v ? fromString(50, v) : right(null)),
      veteran: right,
      about: (v) => fromString(512, v || ''),
      currentRole: right,
      education: right,
      isSerialFounder: right,
    },
    founder,
  );

  return isLeft(r) ? r.value : null;
};

const getValidValue = (
  value: any,
  tenantId: TenantId,
): ICheckVentureValidate => {
  return {
    venture: {
      ventureName: value.ventureName,
      tenantId,
    },
    founderList: value.founders
      .filter((founder: any) => Object.keys(founder).length)
      .map(
        (founder: {
          firstName: string;
          lastName: string;
          email: string;
          emailConfirm: string;
          phone: string;
          linkedInProfile: string;
        }) => ({
          firstName: founder.firstName,
          lastName: founder.lastName,
          email: founder.email,
          tenantId,
        }),
      ),
  };
};

const useStyles = makeStyles(() => ({
  formButtons: {
    justifyContent: 'flex-start',
  },
  loader: {
    margin: '100px 0',
  },
}));

function VentureWizard({
  onSubmit,
  onDone,
  children,
  tenantId,
  initialValues,
  isLoading,
  isUploading,
  consents,
  showCustomAdditionalInfo,
  additionalInfoCustomQuestions,
  showSuccess,
  founderCommitmentEnabled = false,
  goalIntegrationEnabled = false,
  showFounderCustomQuestions,
  founderCustomQuestions,
}: Props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const setSubmitValidationFailed = useScrollOnValidation();
  const [page, setPage] = useState(0);
  const [values, setValues] = useState(
    initialValues || {
      founders: [{ firstName: '', active: true, tempId: uuidv4() }],
      specializations: [],
    },
  );

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues);
    }
  }, [initialValues]);

  const validChildren = React.Children.toArray(children).filter(Boolean);
  const activePage: any = validChildren[page];
  const isSubmitPage = page === validChildren.length - 2;
  const isLastPage = page === validChildren.length - 1;

  const next = (values: any) => {
    const validChildren = React.Children.toArray(children).filter(Boolean);
    setPage(Math.min(page + 1, validChildren.length - 1));
    setValues(values);
    const wizardScroll = document.getElementById('wizard-scroll');
    wizardScroll?.scrollTo(0, 0);
  };

  const previous = () => {
    setPage((prev) => Math.max(prev - 1, 0));
    const wizardScroll = document.getElementById('wizard-scroll');
    wizardScroll?.scrollTo(0, 0);
  };

  const handleSubmit = async (values: any) => {
    if (page === 0) {
      try {
        const checkVentureNameResponse =
          await authAPI.checkVentureNameValidation({
            ventureName: values.ventureName,
            tenantId,
          });

        if (checkVentureNameResponse !== null) {
          enqueueSnackbar(
            'The venture name you have provided already exists on our platform. Please reach out to the admin to address the problem.',
            {
              variant: 'error',
            },
          );
          return;
        }
      } catch (e: any) {
        const messageError = e.response?.data?.message || 'Error';
        enqueueSnackbar(messageError, {
          variant: 'error',
        });

        return;
      }
    }

    if (page === 1) {
      try {
        await authAPI.checkVentureValidate(getValidValue(values, tenantId));
      } catch (e: any) {
        const messageError = e.response?.data?.message || 'Error';
        enqueueSnackbar(messageError, {
          variant: 'error',
        });

        return;
      }
    }

    if (isSubmitPage) {
      await onSubmit(values);
      next(values);
    } else if (isLastPage) {
      await onDone();
      showSuccess();
    } else {
      next(values);
    }
  };

  const validateForm = (values: FormValues) => {
    const errors: Errors = {};
    switch (page) {
      case 0: {
        if (!values.ventureName) {
          errors.ventureName = 'Required';
        }
        if (values.url && !isUrl(values.url)) {
          errors.url = 'Invalid';
        }
        if (values.specializations.length === 0) {
          errors.specializations = 'Required';
        }
        if (
          values.country &&
          isLeft(Sentance.fromString(250, values.country))
        ) {
          errors.country = 'Required';
        }
        if (
          values.address &&
          isLeft(Sentance.fromString(250, values.address))
        ) {
          errors.address = 'Required';
        }
        if (values.city && isLeft(Sentance.fromString(250, values.city))) {
          errors.city = 'Required';
        }
        if (values.state && isLeft(Sentance.fromString(250, values.state))) {
          errors.state = 'Required';
        }
        if (values.zip && !stringToZip(values.zip)) {
          errors.zip = 'Invalid';
        }
        if (
          values.description &&
          isLeft(Sentance.fromString(lengthField.description, values.description))
        ) {
          errors.description = 'Too long';
        }
        if (
          values.mentorshipNeeds &&
          isLeft(Sentance.fromString(lengthField.mentorshipNeeds, values.mentorshipNeeds))
        ) {
          errors.mentorshipNeeds = 'Too long';
        }
        if (
          values.matchingNotes &&
          isLeft(Sentance.fromString(lengthField.matchingNotes, values.matchingNotes))
        ) {
          errors.matchingNotes = 'Too long';
        }
        break;
      }
      case 1: {
        errors.founders = [];

        values.founders.forEach((founder, founderIndex) => {
          if (Object.values(founder).length || founderIndex === 0) {
            const founderErrors = validateFounder(founder);
            (errors.founders as Array<FounderErrors | null>).push(
              founderErrors,
            );
          } else {
            (errors.founders as Array<FounderErrors | null>).push(null);
          }
        });

        // Check for duplicate names and emails
        const foundersWithDuplicateFullName = values.founders.reduce(
          (acc, founder, idx) => {
            if (
              values.founders.filter(
                (f) =>
                  f.firstName === founder.firstName &&
                  f.lastName === founder.lastName,
              ).length > 1
            ) {
              return acc.concat(idx);
            }
            return acc;
          },
          [] as number[],
        );
        if (foundersWithDuplicateFullName.length > 1) {
          errors.founders = errors.founders.map((founder, index) => {
            if (foundersWithDuplicateFullName.includes(index)) {
              return {
                ...founder,
                firstName: 'This name combination is already in use',
                lastName: 'This name combination is already in use',
              };
            }
            return founder;
          });
        }

        const foundersWithDuplicateEmail = values.founders.reduce(
          (acc, founder, idx) => {
            if (
              values.founders.filter((f) => f.email === founder.email).length >
              1
            ) {
              return acc.concat(idx);
            }
            return acc;
          },
          [] as number[],
        );
        if (foundersWithDuplicateEmail.length > 1) {
          errors.founders = errors.founders.map((founder, index) => {
            if (foundersWithDuplicateEmail.includes(index)) {
              return {
                ...founder,
                email: 'This email is already in use',
              };
            }
            return founder;
          });
        }

        // Validate founder custom questions
        if (showFounderCustomQuestions && founderCustomQuestions?.length) {
          values.founders.forEach((founder, founderIndex) => {
            const founderTempId = founder.tempId || `founder-${founderIndex}`;

            founderCustomQuestions.forEach((question) => {
              const fieldKey = `founderCustom_${founderTempId}_${question.id}`;
              const fieldValue = (values as unknown as Record<string, unknown>)[
                fieldKey
              ] as string | undefined;

              if (question.type === CustomQuestionType.SUBTITLE) return;

              try {
                const schema = getCustomQuestionsValidation(
                  question.type,
                  question.isMandatory,
                );
                schema.validateSync(fieldValue);
              } catch (e: any) {
                if (e.name === 'ValidationError') {
                  errors[fieldKey] = e.message;
                }
              }
            });
          });
        }

        if (errors.founders.every((founder) => founder === null)) {
          delete errors.founders;
        }
        break;
      }
      case 2: {
        if (!values.legally) {
          errors.legally = 'Required';
        }
        if (
          !values.businessDescription ||
          length(values.businessDescription || '') === 0
        ) {
          errors.businessDescription = 'Required';
        }
        if (!values.numOfEmployees) {
          errors.numOfEmployees = 'Required';
        }
        if (!values.currentProgress) {
          errors.currentProgress = 'Required';
        }
        if (!values.requiredSupport) {
          errors.requiredSupport = 'Required';
        }
        if (!values.marketSize) {
          errors.marketSize = 'Required';
        }
        if (
          !values.reasonToStartNow ||
          length(values.reasonToStartNow || '') === 0
        ) {
          errors.reasonToStartNow = 'Required';
        }
        if (!values.plans) {
          errors.plans = 'Required';
        }
        if (!values.growthRatio) {
          errors.growthRatio = 'Required';
        }
        if (!values.dedication) {
          errors.dedication = 'Required';
        }
        // Validate yearPlans and obstacles only when Goals Integration is disabled
        if (!goalIntegrationEnabled) {
          if (!values.yearPlans || length(values.yearPlans || '') === 0) {
            errors.yearPlans = 'Required';
          }
          if (!values.obstacles || length(values.obstacles || '') === 0) {
            errors.obstacles = 'Required';
          }
        }
        if (
          !values.mentorshipPlans ||
          length(values.mentorshipPlans || '') === 0
        ) {
          errors.mentorshipPlans = 'Required';
        }
        break;
      }
      case 3: {
        // Page 3 can be Goals (if goalIntegrationEnabled) or Consents (if not)
        // Goals are optional, no validation needed
        if (
          !goalIntegrationEnabled &&
          founderCommitmentEnabled &&
          consents.length
        ) {
          // If Goals are disabled, page 3 is Consents
          consents.forEach((consent) => {
            if (
              // @ts-expect-error
              !values[`field-${consent.id}`] ||
              // @ts-expect-error
              values[`field-${consent.id}`] === 'false'
            ) {
              errors[`field-${consent.id}`] = 'Required';
            }
          });
        }
        // If goalIntegrationEnabled is true, this is the Goals step, no validation needed
        break;
      }
      case 4: {
        // Page 4 can be Consents (if goalIntegrationEnabled) or Upload (if not)
        if (
          goalIntegrationEnabled &&
          founderCommitmentEnabled &&
          consents.length
        ) {
          // If Goals are enabled, page 4 is Consents
          consents.forEach((consent) => {
            if (
              // @ts-expect-error
              !values[`field-${consent.id}`] ||
              // @ts-expect-error
              values[`field-${consent.id}`] === 'false'
            ) {
              errors[`field-${consent.id}`] = 'Required';
            }
          });
        }
        break;
      }
    }
    return errors;
  };

  const customQuestionValidationSchema = useMemo(
    () =>
      additionalInfoCustomQuestions?.length
        ? getCustomQuestionsValidationSchema(additionalInfoCustomQuestions)
        : null,
    [additionalInfoCustomQuestions],
  );

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <Form
      initialValues={values}
      onSubmit={handleSubmit}
      validate={
        page === 2 && showCustomAdditionalInfo && customQuestionValidationSchema
          ? yupValidate(customQuestionValidationSchema)
          : validateForm
      }
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, submitting, submitFailed, dirtySinceLastSubmit }) => {
        setSubmitValidationFailed(
          submitFailed && !dirtySinceLastSubmit && !submitting,
        );
        return (
          <form onSubmit={handleSubmit}>
            {activePage}

            <FormButtons className={classes.formButtons}>
              {page > 0 && !isLastPage && (
                <Button
                  type='button'
                  onClick={previous}
                  data-testid='venture-wizard-form-btn-prev'
                >
                  Previous
                </Button>
              )}
              {isSubmitPage ? (
                <Button
                  type='button'
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid='venture-wizard-form-btn-submit'
                >
                  Submit
                </Button>
              ) : isLastPage ? (
                <Button
                  type='button'
                  onClick={handleSubmit}
                  disabled={isUploading}
                  data-testid='venture-wizard-form-btn-done'
                >
                  Done
                </Button>
              ) : (
                <Button
                  type='button'
                  onClick={handleSubmit}
                  data-testid='venture-wizard-form-btn-next'
                >
                  Next
                </Button>
              )}
            </FormButtons>
          </form>
        );
      }}
    </Form>
  );
}

export const WizardPage = ({ children }: PropsPage) => children;

export default VentureWizard;
